import { useEffect } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  askMeeting,
  deleteMeeting,
  emailMeeting,
  getMeeting,
  getMeetings,
  renameMeetingParticipant,
  summariseMeeting,
  updateMeeting,
} from "../api/meetings";

const PER_PAGE = 50;
const LIST_POLL_MS = 10000;
const MEETING_POLL_MS = 5000;

export const PROCESSING_STATUSES = [
  "PENDING_UPLOAD",
  "PENDING",
  "TRANSCRIBING",
  "SUMMARISING",
];

export const isProcessing = (meeting) =>
  PROCESSING_STATUSES.includes(meeting?.status?.value);

// An abandoned upload sits in PENDING_UPLOAD forever, so don't poll for it
const shouldPoll = (meeting) =>
  isProcessing(meeting) && meeting.status.value !== "PENDING_UPLOAD";

const listKey = ["meetings"];
const meetingKey = (sqid) => ["meeting", sqid];

export function useMeetingsList() {
  const query = useInfiniteQuery(
    listKey,
    async ({ pageParam = 1 }) =>
      (await getMeetings({ page: pageParam, per_page: PER_PAGE })).data,
    {
      getNextPageParam: (last) =>
        last.meta && last.meta.current_page < last.meta.last_page
          ? last.meta.current_page + 1
          : undefined,
      staleTime: 30000,
    },
  );

  const pages = query.data?.pages ?? [];
  // New meetings land on the first page, so only that one is re-fetched while
  // something is processing (refetchInterval would reload every loaded page)
  const polling = !!pages[0]?.data.some(shouldPoll);
  const { refetch } = query;
  useEffect(() => {
    if (!polling) return;
    const id = setInterval(
      () => refetch({ refetchPage: (_page, index) => index === 0 }),
      LIST_POLL_MS,
    );
    return () => clearInterval(id);
  }, [polling, refetch]);

  return {
    ...query,
    meetings: pages.flatMap((p) => p.data),
    total: pages[0]?.meta?.total ?? 0,
  };
}

function findInList(queryClient, sqid) {
  const data = queryClient.getQueryData(listKey);
  for (const page of data?.pages ?? []) {
    const hit = page.data.find((m) => m.sqid === sqid);
    if (hit) return hit;
  }
  return undefined;
}

export function useMeeting(sqid) {
  const queryClient = useQueryClient();
  return useQuery(
    meetingKey(sqid),
    async () => (await getMeeting(sqid)).data.data,
    {
      enabled: !!sqid,
      // The list row shows straight away while the full meeting loads
      placeholderData: () => findInList(queryClient, sqid),
      refetchInterval: (meeting) =>
        meeting && shouldPoll(meeting) ? MEETING_POLL_MS : false,
      retry: (count, error) => error?.response?.status !== 404 && count < 2,
    },
  );
}

// Most meeting endpoints answer with the unhydrated resource (no transcript or
// notes), so merge just the fields that changed rather than replacing the cache.
function useMeetingMutation(sqid, mutationFn, pick) {
  const queryClient = useQueryClient();
  return useMutation(mutationFn, {
    onSuccess: (res) => {
      const updated = res?.data?.data;
      if (updated && pick) {
        queryClient.setQueryData(meetingKey(sqid), (old) =>
          old ? { ...old, ...pick(updated) } : old,
        );
      }
      queryClient.invalidateQueries(meetingKey(sqid));
      queryClient.invalidateQueries(listKey);
    },
  });
}

export const useRenameMeeting = (sqid) =>
  useMeetingMutation(
    sqid,
    (title) => updateMeeting(sqid, { title }),
    (m) => ({ title: m.title }),
  );

export const useUpdateMeetingNotes = (sqid) =>
  useMeetingMutation(
    sqid,
    (notes) => updateMeeting(sqid, { user_notes: notes }),
    (m) => ({ user_notes: m.user_notes }),
  );

export const useSummariseMeeting = (sqid) =>
  useMeetingMutation(
    sqid,
    (templateId) => summariseMeeting(sqid, templateId),
    (m) => ({
      status: m.status,
      error_message: m.error_message,
      summary: m.summary,
      meeting_notes: null,
      sentiment: m.sentiment,
      meeting_notes_template: m.meeting_notes_template,
      meeting_notes_template_id: m.meeting_notes_template_id,
    }),
  );

export const useEmailMeeting = (sqid) =>
  useMeetingMutation(
    sqid,
    (emails) => emailMeeting(sqid, emails),
    (m) => ({ email_recap: m.email_recap }),
  );

// This endpoint returns the hydrated meeting, so it can replace the cache
export function useRenameParticipant(sqid) {
  const queryClient = useQueryClient();
  return useMutation(
    ({ original, name, change }) =>
      renameMeetingParticipant(sqid, original, name, change),
    {
      onSuccess: (res) => {
        queryClient.setQueryData(meetingKey(sqid), res.data.data);
        queryClient.invalidateQueries(listKey);
      },
    },
  );
}

export function useDeleteMeeting() {
  const queryClient = useQueryClient();
  return useMutation((sqid) => deleteMeeting(sqid), {
    onSuccess: (_, sqid) => {
      queryClient.removeQueries(meetingKey(sqid));
      queryClient.invalidateQueries(listKey);
    },
  });
}

export const useAskMeeting = (sqid) =>
  useMutation(
    async (question) => (await askMeeting(sqid, question)).data.data.answer,
  );
